import { useQuery } from "@tanstack/react-query";

/**
 * Simple universal fetch hook using TanStack Query
 * Focused on basic fetching with loading states
 *
 * @example
 * // Basic usage
 * const { data, isLoading, error } = useFetch(getInstitutions, ['institutions']);
 *
 * @example
 * // With parameters
 * const { data, isLoading, error } = useFetch(
 *   () => getPaymentRequest(id),
 *   ['paymentRequest', id]
 * );
 *
 * @example
 * // Conditional fetching
 * const { data, isLoading, error } = useFetch(
 *   () => getMerchant(merchantId),
 *   ['merchant', merchantId],
 *   { enabled: !!merchantId }
 * );
 *
 * @param {Function} queryFn - The function that fetches the data
 * @param {string|Array} queryKey - The query key for caching
 * @param {Object} options - Optional configuration object
 * @param {boolean} options.enabled - Whether the query should execute (default: true)
 * @param {number} options.staleTime - Time in ms before data is considered stale (default: 0 - always refetch)
 * @param {number|boolean} options.retry - Number of retry attempts (default: false - no retries)
 * @returns {Object} Query result with data, isLoading, error
 */
export const useFetch = (queryFn, queryKey, options = {}) => {
  const {
    enabled = true,
    staleTime = 0, // Always consider data stale (always refetch)
    retry = false, // No automatic retries
    ...restOptions
  } = options;

  return useQuery({
    queryKey,
    queryFn,
    enabled,
    staleTime,
    retry,
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: false, // Don't refetch on reconnect
    ...restOptions,
  });
};
